import { type Component, createSignal, mergeProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import { SITE } from '~/data/site'
import type { FooterPropsType } from '~/types/Footer'

const defaultProps: Partial<FooterPropsType> = {
  companyName: SITE.legalName,
  companyDescription: 'Expertos en desarrollo de software y soluciones digitales personalizadas.',
  sections: [
    {
      title: 'Productos',
      links: [
        { label: 'Componentes UI', href: '/componentes' },
        { label: 'Documentación', href: '/docs' },
        { label: 'GitHub', href: 'https://github.com/florezjaramillo' },
        { label: 'Soporte', href: '/contacto' }
      ]
    },
    {
      title: 'Empresa',
      links: [
        { label: 'Sobre Nosotros', href: '/empresa' },
        { label: 'Nuestro Equipo', href: '/equipo' },
        { label: 'Casos de Éxito', href: '/casos' },
        { label: 'Carreras', href: '/carreras' }
      ]
    },
    {
      title: 'Recursos',
      links: [
        { label: 'Blog', href: '/blog' },
        { label: 'Documentación', href: '/docs' },
        { label: 'Centro de Ayuda', href: '/ayuda' },
        { label: 'Contacto', href: '/contacto' }
      ]
    }
  ],
  socialLinks: [
    { platform: 'GitHub', href: SITE.social.github, icon: 'mdi:github' },
    { platform: 'Twitter', href: SITE.social.twitter, icon: 'mdi:twitter' },
    { platform: 'Instagram', href: SITE.social.instagram, icon: 'mdi:instagram' }
  ],
  contactInfo: {
    email: SITE.contact.email,
    phone: SITE.contact.phone,
    address: SITE.contact.location
  },
  copyrightText: `© ${new Date().getFullYear()} ${SITE.legalName}. Todos los derechos reservados.`,
  showNewsletter: true,
  newsletterPlaceholder: 'Tu email para recibir novedades'
}

const Footer: Component<FooterPropsType> = props => {
  const merged = mergeProps(defaultProps, props)
  const [email, setEmail] = createSignal('')

  const handleNewsletterSubmit = (e: Event) => {
    e.preventDefault()
    if (merged.onNewsletterSubmit) {
      merged.onNewsletterSubmit(email())
    }
    setEmail('')
  }

  return (
    <footer class={twMerge('bg-slate-900 text-white py-12 px-4 md:px-8', 'dark:bg-slate-950', merged.class)} {...merged}>
      <Container class="px-0">
        {/* Main Footer Content */}
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div class="lg:col-span-1">
            <h3 class="text-xl font-bold mb-4 text-primary-400">{merged.companyName}</h3>
            <p class="text-slate-300 mb-4">{merged.companyDescription}</p>
            {merged.contactInfo && (
              <div class="space-y-2 text-sm">
                {merged.contactInfo.email && (
                  <div class="flex items-center gap-2">
                    <Icon icon="material-symbols-light:mail-outline" size={16} />
                    <a href={`mailto:${merged.contactInfo.email}`} class="hover:text-primary-400 transition-colors">
                      {merged.contactInfo.email}
                    </a>
                  </div>
                )}
                {merged.contactInfo.phone && (
                  <div class="flex items-center gap-2">
                    <Icon icon="material-symbols-light:call-outline" size={16} />
                    <a href={`tel:${merged.contactInfo.phone}`} class="hover:text-primary-400 transition-colors">
                      {merged.contactInfo.phone}
                    </a>
                  </div>
                )}
                {merged.contactInfo.address && (
                  <div class="flex items-center gap-2">
                    <Icon icon="material-symbols-light:location-on-outline" size={16} />
                    <span>{merged.contactInfo.address}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sections */}
          {merged.sections?.map(section => (
            <div>
              <h4 class="text-lg font-semibold mb-4 text-slate-200">{section.title}</h4>
              <ul class="space-y-2">
                {section.links.map(link => (
                  <li>
                    <a href={link.href} class="text-slate-300 hover:text-primary-400 transition-colors flex items-center gap-2">
                      {link.icon && <Icon icon={link.icon} size={14} />}
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          {merged.showNewsletter && (
            <div class="lg:col-span-1">
              <h4 class="text-lg font-semibold mb-4 text-slate-200">Newsletter</h4>
              <p class="text-slate-300 mb-4">Suscríbete para recibir las últimas novedades y actualizaciones.</p>
              <form onSubmit={handleNewsletterSubmit} class="space-y-3">
                <Input
                  type="email"
                  placeholder={merged.newsletterPlaceholder}
                  value={email()}
                  onInput={e => setEmail(e.currentTarget.value)}
                  class="bg-slate-800 border-slate-700 text-white placeholder-slate-400"
                />
                <Button type="submit" variant="contained" color="primary" fullWidth endIcon="material-symbols-light:arrow-forward">
                  Suscribirse
                </Button>
              </form>
            </div>
          )}
        </div>

        <div class="flex justify-center gap-4 mb-8">
          {merged.socialLinks?.map(social => (
            <a href={social.href} target="_blank" rel="noopener noreferrer" class="text-slate-400 hover:text-primary-400 transition-colors p-2 rounded-full hover:bg-slate-800" title={social.platform}>
              {(() => {
                const icon = social.icon
                if (typeof icon === 'string') {
                  return <Icon icon={icon} size={24} />
                } else {
                  return (icon as Component<{ size: number }>)({ size: 24 })
                }
              })()}
            </a>
          ))}
        </div>

        {/* Bottom Bar */}
        <div class="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-center items-center gap-4">
          <p class="text-slate-400 text-sm">{merged.copyrightText}</p>
        </div>
      </Container>
    </footer>
  )
}

export default Footer
